import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useWallet } from "@/entities/wallet";
import { ROUTES } from "@/shared/config";
import { formatMoney } from "@/shared/lib";
import { AmountInput, Button } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Шаг 1 вывода. Макета нет: каркас `Launch Amount` (22:157) плюс
 * `AmountInput/Trading` (18:47) — DESIGN.md §5.
 *
 * Доступная сумма приходит готовой в `available_for_withdrawal` (BE-14): это
 * баланс за вычетом уже поданных заявок, и считать её на клиенте нельзя
 * (правило ТЗ №5). Проверку суммы делает бэкенд.
 */
export function WithdrawAmountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const { data, isPending, isError, refetch } = useWallet();

  if (isPending) return <ScreenSkeleton blocks={[96, 80, 50]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{ label: t("states.error.action"), onClick: () => void refetch() }}
      />
    );
  }

  const assets = Object.keys(data.deposit_assets ?? {});

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("withdraw.title")} subtitle={t("withdraw.subtitle")} />

      <section className="flex flex-col gap-1 rounded-2xl border border-border-subtle bg-background-elevated p-4">
        <span className="text-caption font-medium text-text-secondary">{t("withdraw.available")}</span>
        <span className="text-h2 font-bold text-text-primary">
          {formatMoney(data.available_for_withdrawal, { suffix: "USDT" })}
        </span>
        <span className="text-caption font-normal text-text-secondary">
          {t("withdraw.availableHint")}
        </span>
      </section>

      <AmountInput
        value={amount}
        onChange={setAmount}
        hint={t("withdraw.minWithdrawal", {
          amount: formatMoney(data.min_withdrawal, { suffix: "USDT" }),
        })}
      />

      <Button
        size="cta"
        block
        disabled={amount === "" || assets.length === 0}
        onClick={() => void navigate(ROUTES.withdrawAddress, { state: { amount } })}
      >
        {t("common.next")}
      </Button>
    </div>
  );
}
